import { z } from 'zod';

import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

export const zFilter = z.object({
  where_sql_template: z.string().nullable(),
});

const zGranularity = z.enum(['day', 'week', 'month', 'quarter', 'year']);

const zMetricTimeWindow = z.object({
  count: z.number(),
  granularity: zGranularity,
});

const zMetricInputMeasure = z.object({
  name: z.string(),
  alias: z.string().nullable(),
  filter: zFilter.nullable(),
});

const zMetricInput = z.object({
  name: z.string(),
  filter: zFilter.nullable(),
  alias: z.string().nullable(),
  offset_window: zMetricTimeWindow.nullable(),
  offset_to_grain: zGranularity.nullable(),
});

/**
 * https://schemas.getdbt.com/dbt/manifest/v10/index.html#metrics_additionalProperties_type_params_measures
 */
export const zTypeParams = z.object({
  expr: z.string().nullable(),
  window: zMetricTimeWindow.nullable(),
  measure: zMetricInputMeasure.nullable(),
  metrics: zMetricInput.array().nullable(),
  measures: zMetricInputMeasure.nullable(),
  numerator: zMetricInputMeasure.nullable(),
  denominator: zMetricInputMeasure.nullable(),
  grain_to_date: zGranularity.nullable(),
  input_measures: zMetricInputMeasure.array(),
});

type Result = ReturnType<typeof useMetricByEnvironment>[0];

export type MetricResponse = NonNullable<Result['data']>;

export type RequestedMetricAppliedNode = GetAppliedNode<'metrics', MetricResponse>;

export type RequestedMetricDefinitionNode = GetDefinitionNode<
  'metrics',
  MetricResponse
>;

export const GetMetricByUniqueId = graphql(/* GraphQL */ `
  query Metric($environmentId: BigInt!, $userId: BigInt, $uniqueId: String!) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        metrics(first: 1, filter: { uniqueIds: [$uniqueId] }) {
          edges {
            node {
              fqn
              name
              type
              filter
              formula
              runGeneratedAt
              typeParams
              uniqueId
              tags
              description
              meta
              projectId
              group
              packageName
              filePath
              patchPath
              parents {
                uniqueId
                resourceType
                name
                __typename
              }
              children {
                uniqueId
              }
            }
          }
        }
      }
    }
  }
`);

type MetricByEnvironmentParams = {
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  uniqueId: string;
  pause?: boolean;
};

export const useMetricByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: MetricByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetMetricByUniqueId,
    variables: { environmentId, userId: userId as number | null, uniqueId },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    definition: query[0].data?.environment.definition?.lastUpdatedAt,
  });

  return query;
};
